"use client";

import React from "react";
import { HiOutlineChevronDown } from "react-icons/hi2";
import Link from "next/link";

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQProps {
  items: FAQItem[];
}

function renderTextWithLinks(text: string) {
  // Regular expression to match URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;

  // Split the text by URLs and map each part
  const parts = text.split(urlRegex);

  return parts.map((part, index) => {
    // If the part matches URL pattern, render it as a link
    if (part.match(urlRegex)) {
      return (
        <Link
          key={index}
          href={part}
          className="text-text-body-primary hover:underline"
          target="_blank"
        >
          {part}
        </Link>
      );
    }
    // Otherwise render as plain text
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}

export function FAQ({ items }: FAQProps) {
  return (
    <div className="mx-auto w-full lg:w-10/12 space-y-2">
      {items.map((item, index) => (
        <details
          key={index}
          className="group transition duration-300 ease-in-out bg-surface-bg border-b border-border-default"
        >
          <summary className="flex justify-between items-center cursor-pointer p-4 rounded-lg list-none">
            <span className="text-left text-text-body-primary font-medium pr-6">
              {item.question}
            </span>
            <HiOutlineChevronDown className="shrink-0 transform group-open:rotate-180 transition-transform duration-300" />
          </summary>
          <div className="p-4 bg-surface-default rounded-b-lg">
            <p className="text-base text-text-body-subtle">{renderTextWithLinks(item.answer)}</p>
          </div>
        </details>
      ))}
      <style jsx>{`
        details > summary::-webkit-details-marker,
        details > summary::marker {
          display: none;
        }

        details > summary {
          position: relative;
        }

        details > summary:focus {
          outline: none;
        }

        details > div {
          display: none;
        }

        details[open] > div {
          display: block;
        }
      `}</style>
    </div>
  );
}
